'use strict';

import {
	Box3,
	MathUtils,
	OrthographicCamera,
	Vector2,
	Vector3,
	MOUSE,
	TOUCH,
	WebGLRenderTarget,
	Sphere,
	Color
} from 'three';
import { BaseElement } from '../interfaces/BaseElement';
import { OrbitControls } from '../../libs/OrbitControls';
import { assignDeep, GSAPAnimationMixer, isMobile, settingsData, threeJsVariables } from '../../settings';
import { getCurrentCameraDistance, getWorldData } from '../controls/orbitControls';
import gsap from 'gsap';
import { calculateDeltaFromVec3 } from '../../functions';
import { updateShadowMap } from '../threejs/lights';
import { ViewMode } from '../../common';
import { Lerp } from '../projectTime/Lerp';
import { getCurrentLangDirection } from '../components/langSwitcher/langSwitcher';

const fullMapBox = new Box3();
const regionBox = new Box3();
const size = new Vector3();
const center = new Vector3();
const pointerDown = new Vector3();
const pointerUp = new Vector3();
window.cc = new Color();


const DEFAULT_PARAMS = {
	active: false,
	showInCylinder: false,
	hideInOrbit: false,
	ui: {
		width: 120,
		height: 120,
		position: {
			bottom: 20,
			left: 20
		},
		scale: 5
	},
	map: {
		size: 500,
		maxZoom: 5,
		zoomSpeed: 4,
		initialZoom: 1,
		initialCenter: [0,0],
		color: ''
	},
	camera: {
		changeTarget: false,
		autoTarget: false
	},
	blackList: []
};

export class MiniMap {
	
	constructor (scene,renderer) {
		this.options = assignDeep({},DEFAULT_PARAMS,config.features.miniMap);
		
		if (isMobile) {
			this.options.active = false;
		}
		
		this.originOptions = JSON.parse(JSON.stringify(this.options));
		
		this.scene = scene;
		this.renderer = renderer;
		this.markers = [];
		this.opened = false;
		
		this.camera = new OrthographicCamera(  -2,  2,  2,  -2,-1000,1000);
		this.camera.position.set(this.options.map.initialCenter[0],0,this.options.map.initialCenter[1]);
		this.camera.lookAt(0,-1,0);
		this.camera.zoom = this.options.map.initialZoom;
		
		this.rt = new WebGLRenderTarget();
		this.canvas = document.createElement( 'canvas' );
		this.ctx = this.canvas.getContext( '2d' );
		this.color = null;
		this.sceneOriginColor = threeJsVariables.scene.background;
		
		if (this.options.map.color) {
			this.color = new Color(this.options.map.color);
		}
		
		this.camera.updateMatrixWorld();
		this.camera.updateProjectionMatrix();
	}
	
	appendTo(parent) {
		if (!this.active) return;
		
		this.container = new BaseElement()
			.addClass('scene-mini-map')
			.event('pointerdown',this.pointerDown.bind(this))
			.event('pointerup',this.pointerUp.bind(this))
			.event('mouseover',this.hover.bind(this))
			.event('mouseleave',this.hover.bind(this))
			// .addStyle('width',`${this.options.ui.width}px`)
			// .addStyle('height',`${this.options.ui.height}px`)
			.appendTo(parent);
		
		// Object.entries(this.options.ui.position).forEach(([key,value]) => {
		// 	this.container.addStyle(key,`${value}px`);
		// });
		
		this.container = this.container.DOMElement;
		this.setContainerSize(this.options.ui.width,this.options.ui.height);
		this.container.appendChild(this.canvas);
		
		this.controls = new OrbitControls(this.camera,this.container);
		this.controls.target.set(this.options.map.initialCenter[0],-1,this.options.map.initialCenter[1]);
		this.controls.screenSpacePanning = false;
		this.controls.enableRotate = false;
		this.controls.minAzimuthAngle = config.threejs.compass.correction;
		this.controls.maxAzimuthAngle = config.threejs.compass.correction;
		// this.controls.reverseControls = true;
		this.controls.zoomSpeed = this.options.map.zoomSpeed;
		this.controls.minZoom = 1;
		this.controls.maxZoom = this.options.map.maxZoom;
		this.controls.mouseButtons = {
			LEFT: MOUSE.PAN,
			MIDDLE: MOUSE.DOLLY
		};

		this.controls.touches = {
			ONE: TOUCH.PAN,
			TWO: TOUCH.DOLLY_PAN
		};

		this.controls.enableDamping = false;
		this.controls.addEventListener('change',this.controlsHandler.bind(this));
	}
	
	hover(e) {
		const zoomScope = this.findMarker('zoom');
		if (e.type === 'mouseover') {
			this.setContainerSize(this.originOptions.ui.width * this.options.ui.scale, this.originOptions.ui.height * this.options.ui.scale);
			zoomScope.enabled = true;
			zoomScope.show();
			this.updateMarkers();
		} else {
			this.setContainerSize(this.originOptions.ui.width,this.originOptions.ui.height);
			zoomScope.hide();
			zoomScope.enabled = false;
			// this.hide();
		}
	}
	
	pointerDown(e) {
		const x = e.layerX / this.options.ui.width * 2 - 1;
		const y = - (e.layerY / this.options.ui.height) * 2 + 1;
		pointerDown.set(x,y,0);
	}
	
	pointerUp(e) {
		const x = e.layerX / this.options.ui.width * 2 - 1;
		const y = - (e.layerY / this.options.ui.height) * 2 + 1;
		pointerUp.set(x,y,0);
		
		this.clickHandler(e);
	}
	
	clickHandler(e) {
		if (!pointerDown.equals(pointerUp)) return;
		
		switch (e.target.tagName) {
			case 'CANVAS':
				const scope = this.findMarker('zoom');
				let world = pointerUp.unproject(this.camera);
				const {cameraVector,orbitControlsVector} = getWorldData();
				world.y = orbitControlsVector.y;

				const mixer = GSAPAnimationMixer.registerNewMixer('mini-map-click');
				let direction,positionVector;


				if (this.options.camera.changeTarget) {
					direction = new Vector3().copy(world).normalize().add(world);
					const length = direction.length();
					positionVector = new Vector3().copy(direction).setLength(length * 0.8);

					const target = gsap.to(threeJsVariables.orbitControls.target,{
						x: direction.x,
						y: direction.y,
						z: direction.z,
						duration: GSAPAnimationMixer.duration,
						ease: GSAPAnimationMixer.ease
					});

					mixer.addAnimation(target);

					const position = gsap.to(threeJsVariables.perspectiveCamera.position,{
						x: positionVector.x,
						y: positionVector.y,
						z: positionVector.z,
						duration: GSAPAnimationMixer.duration,
						ease: GSAPAnimationMixer.ease
					});

					mixer.addAnimation(position);


				} else {
					const clamped = scope.clampPoint(world);
					
					if (!clamped.equals(world)) {
						clamped.setLength(clamped.length() * 0.95);
						world.copy(clamped);
					}
					
					const delta = calculateDeltaFromVec3(world);
					const currentDelta = threeJsVariables.orbitControls.getAzimuthalAngle();
					const thetaLerp = new Lerp(currentDelta,currentDelta + delta);
					const distanceLerp = new Lerp(getCurrentCameraDistance(),world.distanceTo(threeJsVariables.orbitControls.target));
					const cameraProgress = {
						value: 0
					};
					
					direction = new Vector3();
					
					const positionAnimation = gsap.to(cameraProgress,{
						value: 1,
						duration: GSAPAnimationMixer.duration,
						ease: GSAPAnimationMixer.ease,
						onUpdate: () => {
							threeJsVariables.orbitControls.setAzimuthAngle(thetaLerp.lerp(cameraProgress.value));
							direction.subVectors(threeJsVariables.perspectiveCamera.position,threeJsVariables.orbitControls.target);
							direction.normalize().setLength(distanceLerp.lerp(cameraProgress.value)).add(threeJsVariables.orbitControls.target);
							threeJsVariables.perspectiveCamera.position.copy(direction);
						}
					});
					
					mixer.addAnimation(positionAnimation);
					
				}

				// mixer.addAnimation(p);

				mixer.play();

				break;
		}
		
	}
	
	controlsHandler() {
		this.zoom();
		this.setPan();
		this.render();
		this.updateMarkers();
		// this.helper.update();
	}
	
	computeBoundingBox() {
		if (!this.active) return;
		
		if (this.options.map.size === 'auto') {
			const sphere = this.scene.getObjectByName('Background');
			if (sphere) {
				fullMapBox.setFromObject(sphere);
			} else {
				
				if (this.options.camera.autoTarget) {
					fullMapBox.setFromCenterAndSize(
						new Vector3(threeJsVariables.orbitControls.target.x,0,threeJsVariables.orbitControls.target.z),
						new Vector3( 500,500, 500)
					);
				} else {
					fullMapBox.setFromCenterAndSize(
						new Vector3(this.options.map.initialCenter[0],0,this.options.map.initialCenter[1]),
						new Vector3( 500,500, 500)
					);
				}
			}
			
		} else {
			const size = this.options.map.size;
			
			if (this.options.camera.autoTarget) {
				fullMapBox.setFromCenterAndSize(
					new Vector3(threeJsVariables.orbitControls.target.x,0,threeJsVariables.orbitControls.target.z),
					new Vector3( size,size, size)
				);
			} else {
				fullMapBox.setFromCenterAndSize(
					new Vector3(this.options.map.initialCenter[0],0,this.options.map.initialCenter[1]),
					new Vector3( size,size, size)
				);
			}
		}
		fullMapBox.getSize(size);
		fullMapBox.getCenter(center);
		
		size.ceil();
		regionBox.copy(fullMapBox);
		this.setSize();
	}
	
	setContainerSize(width,height) {
		this.options.ui.width = width;
		this.options.ui.height = height;
		
		this.container.style.width = `${this.options.ui.width}px`;
		this.container.style.height = `${this.options.ui.height}px`;
		this.updateMarkers();
	}
	
	setSize() {
		this.camera.left = size.x / -2;
		this.camera.right = size.x / 2;
		this.camera.top = size.z / 2;
		this.camera.bottom = size.z / -2;
		// this.camera.position.copy(center);
		this.camera.updateMatrixWorld();
		this.camera.updateProjectionMatrix();
		this.rt.setSize(size.x,size.z);
		
		// if (!this.helper) {
		// 	this.helper = new CameraHelper( this.camera );
		// 	this.scene.add(this.helper);
		// } else {
		// 	this.helper.update();
		// }
		
		this.controlsHandler();
		this.controls.update();
		
		for (let i = 0; i < this.markers.length; i++) {
			this.markers[i].setSize(size);
		}
	}
	
	setPan() {
		const min = new Vector3().copy(fullMapBox.min);
		min.y = 0;
		min.x = min.x + this.camera.right / this.camera.zoom;
		min.z = min.z + this.camera.top / this.camera.zoom;
		const max = new Vector3().copy(fullMapBox.max);
		max.x = max.x - this.camera.right / this.camera.zoom;
		max.z = max.z - this.camera.top / this.camera.zoom;
		max.y = 0;
		
		this.controls.maxPan = max;
		this.controls.minPan = min;
	}
	
	zoom() {
		const min = new Vector3().copy(fullMapBox.min);
		const max = new Vector3().copy(fullMapBox.max);
		min.x = min.x / this.camera.zoom;
		min.z = min.z / this.camera.zoom;
		max.x = max.x / this.camera.zoom;
		max.z = max.z / this.camera.zoom;
		regionBox.set(min,max);
		
		const zoomSize = new Vector3();
		regionBox.getSize(zoomSize);
		regionBox.setFromCenterAndSize(this.camera.position,zoomSize);
	}
	
	render() {
		if (settingsData.activeViewMode === ViewMode.Orbit) {
			if (!this.options.blackList.includes('async env')) {
				threeJsVariables.tilesRenderer?.setCamera(this.camera);
				threeJsVariables.tilesRenderer?.setResolutionFromRenderer(this.camera, this.renderer);
				threeJsVariables.tilesRenderer?.update();
			}
			this.objectsVisibility('hide');
			updateShadowMap();
			if (this.color) {
				threeJsVariables.scene.background = this.color;
			}
			this.renderer.setRenderTarget(this.rt);
			this.renderer.render(this.scene, this.camera);
			this.renderer.setRenderTarget(null);
			this.objectsVisibility('show');
			updateShadowMap();
			threeJsVariables.scene.background = this.sceneOriginColor;
			threeJsVariables.tilesRenderer?.deleteCamera(this.camera);
			
		} else {
			if (threeJsVariables.slicer.active) {
				threeJsVariables.slicer.restoreXZ('immediately');
			}
			
			if (threeJsVariables.sceneObjectsSlicer.active) {
				threeJsVariables.sceneObjectsSlicer.restoreXZ('immediately');
			}
			
			this.objectsCylinderVisibility('show');
			updateShadowMap();
			this.renderer.setRenderTarget(this.rt);
			this.renderer.render(this.scene, this.camera);
			this.renderer.setRenderTarget(null);
			
			if (threeJsVariables.slicer.active) {
				threeJsVariables.slicer.cropXZ('immediately');
			}
			
			if (threeJsVariables.sceneObjectsSlicer.active) {
				threeJsVariables.sceneObjectsSlicer.cropXZ('immediately');
			}
			this.objectsCylinderVisibility('hide');
			updateShadowMap();
		}
		
		this.canvas.width = size.x;
		this.canvas.height = size.z;
		
		const pixels = new Uint8Array( 4 * size.x * size.z );
		
		this.renderer.readRenderTargetPixels( this.rt, 0, 0, size.x, size.z,pixels);
		const imageData = new ImageData( new Uint8ClampedArray(pixels), size.x, size.z);
		this.ctx.putImageData( imageData, 0, 0 );
	}
	
	objectsVisibility(state) {
		const list = this.options.blackList.slice();
		list.push('Background');
		list.push('Environment');
		
		if (threeJsVariables.bundleConfig && threeJsVariables.bundleConfig.priority) {
			Object.entries(threeJsVariables.bundleConfig.priority).forEach(([name,position]) => {
				if (position === 2) {
					list.push(name);
				}
			});
		}
		
		for (let i = 0; i < list.length; i++) {
			const object = threeJsVariables.scene.getObjectByName(list[i]);
			if (object) {
				object.visible = state === 'show';
			}
		}
	}
	
	objectsCylinderVisibility(state) {
		const list = this.options.blackList.slice();
		
		if (threeJsVariables.bundleConfig && threeJsVariables.bundleConfig.priority) {
			Object.entries(threeJsVariables.bundleConfig.priority).forEach(([name,position]) => {
				if (position === 1 && !name.startsWith(config.resources.bundle.building.name)) {
					list.push(name);
				}
			});
		}
		
		for (let i = 0; i < list.length; i++) {
			const object = threeJsVariables.scene.getObjectByName(list[i]);
			if (object) {
				object.traverse(el => {
					if (el.isMesh) {
						el.visible = state === 'show';
					}
				});
			}
		}
	}
	
	updateMarkers() {
		if (this.active && this.openedMap) {
			for (let i = 0; i < this.markers.length; i++) {
				this.markers[i].update(regionBox,this.camera,this.controls,this.options.ui.width,this.options.ui.height);
			}
		}
	}
	
	show() {
		if (!this.active) return;
		
		if (this.options.hideInOrbit && settingsData.activeViewMode === ViewMode.Orbit) {
			this.hide();
			return;
		}
		
		this.openedMap = true;
		this.updateMarkers();
	}
	
	hide() {
		if (!this.active) return;
		
		if (this.options.showInCylinder && settingsData.activeViewMode === ViewMode.Cylinder) {
			this.show();
			return;
		}
		
		this.openedMap = false;
		for (let i = 0; i < this.markers.length; i++) {
			this.markers[i].hide();
		}
	}
	
	addMarker(scope) {
		if (!this.active) return;
		this.markers.push(scope);
		this.container.appendChild(scope.marker);
	}
	
	findMarker(id) {
		return this.markers.find(marker => marker.id === id);
	}
	
	get active() {
		return this.options.active;
	}
	
	set openedMap(value) {
		this.opened = value;
		this.container.style.display = value ? '' : 'none';
	}
	
	get openedMap() {
		return this.opened;
	}
}


class Marker {
	constructor (id) {
		this.id = id;
		this.marker = new BaseElement()
			.addClass('scene-mini-map__marker')
			.addStyle('display','none')
			.addAttribute('id',this.id)
			.DOMElement;
		this.position = new Vector3();
		this.mapPosition = null;
		this.active = false;
		this.size = new Vector3();
	}
	
	setSize(value) {
		this.size.copy(value);
	}
	
	setContent(content) {
		this.marker.innerHTML = '';
		this.marker.appendChild(content);
	}
	
	setWorldPosition(vec3) {
		this.position.copy(vec3).setY(center.y);
	}
	
	update(bbox,camera,controls,mapWidth,mapHeight) {
		if (this.enabled) {
			if (bbox.containsPoint(this.position)) {
				this.show();
				this.mapPosition = this.position.project(camera);
				this.marker.style.left =  (this.mapPosition.x * (mapWidth / 2) + mapWidth / 2 ) + 'px';
				this.marker.style.top =  mapHeight - (this.mapPosition.y * (mapHeight / 2) + mapHeight / 2 ) + 'px';
				
			} else {
				this.hide();
			}
		}
	}
	
	hide() {
		this.marker.style.display = 'none';
	}
	
	show() {
		this.marker.style.display = '';
	}
	
	get enabled() {
		return this.active;
	}
	
	set enabled(value) {
		this.active = value;
	}
}

export class CameraMarker extends Marker{
	constructor (id,originCamera,originOrbitControls) {
		super(id);
		
		this.originCamera = originCamera;
		this.originControls = originOrbitControls;
		this.target = new Vector3();
		this.initialFov = this.originCamera.fov;
		
		this.radar = new BaseElement()
			.addClass('mini-map__origin-camera')
			.addStyle('border-width', `0 ${this.originCamera.fov / 2}px ${this.originCamera.fov / 2}px ${this.originCamera.fov / 2}px`)
			.DOMElement;
		
		this.setContent(this.radar);
		
	}
	
	setWorldPosition() {
		this.position.copy(this.originCamera.position);
	}
	
	update(bbox,camera,controls,mapWidth,mapHeight) {
		if (this.enabled) {
			this.setWorldPosition();
			if (bbox.containsPoint(this.position)) {
				this.show();
				this.mapPosition = this.position.project(camera);
				this.marker.style.left =  (this.mapPosition.x * (mapWidth / 2) + mapWidth / 2 ) + 'px';
				this.marker.style.top =  mapHeight - (this.mapPosition.y * (mapHeight / 2) + mapHeight / 2 ) + 'px';
				this.marker.style.transform = `translate(-50%,100%) rotate(${MathUtils.radToDeg(Math.PI - (this.originControls.getAzimuthalAngle() - controls.maxAzimuthAngle))}deg)`;
				if (this.initialFov !== this.originCamera.fov) {
					this.radar.style.borderWidth = `0 ${this.originCamera.fov / 2}px ${this.originCamera.fov / 2}px ${this.originCamera.fov / 2}px`;
					this.initialFov = this.originCamera.fov;
				}
				
			} else {
				this.hide();
			}
		}
	}
	
}

export class ZoomArea extends Marker{
	constructor (id) {
		super(id);
		
		this.orbitControls = threeJsVariables.orbitControls;
		this.trackballControls = threeJsVariables.trackballControls;
		
		this.content = new BaseElement()
			.addClass('limit-area-content')
			.DOMElement;
		
		const maxZoom = (this.trackballControls.maxDistance * 1.2) / 2;
		this.sphere = new Sphere(this.orbitControls.target,maxZoom * 2 / 1.2);
		
		new BaseElement()
			.addClass('limit-area-zoom','limit-max-zoom')
			.addStyle('width',`${maxZoom}px`)
			.addStyle('height',`${maxZoom}px`)
			.appendTo(this.content);
		
		// new BaseElement()
		// 	.addClass('limit-area-zoom','limit-min-zoom')
		// 	.addStyle('width',`${this.trackballControls.minDistance / 2}px`)
		// 	.addStyle('height',`${this.trackballControls.minDistance / 2}px`)
		// 	.appendTo(this.content);
		
		this.setContent(this.content);
		
	}
	
	setWorldPosition() {
		this.position.copy(this.orbitControls.target);
	}
	
	update(bbox,camera,controls,mapWidth,mapHeight) {
		if (this.enabled) {
			this.setWorldPosition();
			this.show();
			this.mapPosition = this.position.project(camera);
			this.marker.style.left =  (this.mapPosition.x * (mapWidth / 2) + mapWidth / 2 ) + 'px';
			this.marker.style.top =  mapHeight - (this.mapPosition.y * (mapHeight / 2) + mapHeight / 2 ) + 'px';
			this.content.style.transform = `translate(-50%,-50%) scale(${camera.zoom * 2 * (mapWidth / this.size.x) * (Math.PI / 2)})`;
			// this.max.style.width = `${this.maxZoom * (1000 / this.size.x)}px`;
			// this.max.style.height = `${this.maxZoom * (1000 / this.size.y)}px`;
			// this.sphere.center.copy(this.orbitControls.target).setY(0);
		}
	}
	
	clampPoint(vec) {
		const clamped = new Vector3();
		this.sphere.clampPoint(vec,clamped);
		return clamped;
	}
	
}

export class PanArea extends Marker{
	constructor (id) {
		super(id);
		
		this.orbitControls = threeJsVariables.orbitControls;
		
		if (this.orbitControls.maxPan && this.orbitControls.minPan) {
			
			const width = (Math.abs(this.orbitControls.minPan.x) + this.orbitControls.maxPan.x) / 2 * 1.2;
			const height = (Math.abs(this.orbitControls.minPan.z) + this.orbitControls.maxPan.z) / 2 * 1.2;
			
			this.area = new BaseElement()
				.addClass('limit-area-pan')
				.addStyle('width',`${width}px`)
				.addStyle('height',`${height}px`)
				.DOMElement;
			
			this.setContent(this.area);
		}
	}
	
	update(bbox,camera,controls,mapWidth,mapHeight) {
		if (this.enabled) {
			this.show();
			this.mapPosition = this.position.project(camera);
			this.marker.style.left =  (this.mapPosition.x * (mapWidth / 2) + mapWidth / 2 ) + 'px';
			this.marker.style.top =  mapHeight - (this.mapPosition.y * (mapHeight / 2) + mapHeight / 2 ) + 'px';
			this.area.style.transform = `translate(-50%,-50%) scale(${camera.zoom}) rotate(${MathUtils.radToDeg(Math.PI - config.threejs.compass.correction)}deg)`;
		}
	}
}
